import { cva, type VariantProps } from "class-variance-authority";
import type { ComponentPropsWithRef } from "react";

import { cn } from "../lib/cn";

/**
 * Système de Surfaces de Composants Natifs (@one-nexty design-system)
 *
 * Définit le matériau, la profondeur, la spécularité et la réactivité d'une
 * surface de composant (Card, Input, Dialog, Popover, Navbar, etc.).
 *
 * Distinction essentielle :
 * - Page / Canvas surface : le fond d'arrière-plan de la page (--nx-color-surface).
 * - Component Surface : le matériau et le relief propres au composant lui-même.
 */
/**
 * `backdrop-filter` est volontairement absent de la liste des propriétés en
 * transition. Il est composé par le GPU, mais l'interpoler oblige le navigateur
 * à re-flouter tout ce qui se trouve derrière la surface à chaque image — c'est
 * l'effet le plus coûteux du répertoire CSS. Le flou est donc posé une fois,
 * et c'est l'opacité du matériau qui varie.
 *
 * `box-shadow` en est absent pour la même raison : une ombre interpolée se
 * repeint image par image. Les variantes interactives passent par `lift`, qui
 * fait FONDRE une ombre déjà peinte sur un pseudo-élément.
 */
const surfaceVariants = cva(
  "transform-gpu transition-[transform,opacity,background-color,border-color] duration-normal ease-standard",
  {
    variants: {
      variant: {
        /** Surface neutre affleurante avec le canevas. */
        flat: "bg-surface border border-border/50 text-fg",
        /** Surface surélevée tactile avec arête lumineuse supérieure spéculaire et ombre douce. */
        raised: "bg-surface-raised border border-border/80 text-fg shadow-xs inset-shadow-raised",
        /** Surface en retrait / puit (inputs, blocs de code, en-têtes de tableaux). */
        sunken: "bg-surface-sunken border border-border/40 text-fg inset-shadow-control",
        /** Matériau verre dépoli (glassmorphism) natif avec flou d'arrière-plan et bordure spéculaire. */
        glass:
          "bg-surface-raised/80 dark:bg-surface-raised/65 backdrop-blur-md border border-border/60 text-fg shadow-xs inset-shadow-glass",
        /** Surface flottante pour popovers, menus déroulants et infobulles. */
        floating: "bg-surface-raised border border-border text-fg shadow-md",
        /** Surface de surélévation maximale pour modales, dialogs et bottom sheets. */
        overlay: "bg-surface-raised border border-border-strong/30 text-fg shadow-overlay",
        /** Surface interactive pour boutons et éléments de contrôle. */
        control:
          "bg-surface-raised border border-border-strong text-fg shadow-xs inset-shadow-raised",
        /** Surface teintée de marque très légère (chips, notifications, onglets actifs). */
        subtle: "bg-accent-subtle border border-accent/20 text-accent-fg",
      },
      interactive: {
        // `lift` fond une ombre déjà peinte au lieu d'en interpoler une ;
        // `will-change` promeut la couche au survol seulement, jamais à demeure.
        true: "lift cursor-pointer hover:-translate-y-px hover:will-change-transform hover:border-border-strong active:translate-y-0 active:duration-instant focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
        false: "",
      },
    },
    defaultVariants: {
      variant: "raised",
      interactive: false,
    },
  },
);

export type SurfaceVariant = NonNullable<VariantProps<typeof surfaceVariants>["variant"]>;

export interface SurfaceProps
  extends ComponentPropsWithRef<"div">,
    VariantProps<typeof surfaceVariants> {}

/**
 * Composant Surface — Primitif de conteneur natif.
 *
 * @example
 * <Surface variant="glass">Contenu translucide</Surface>
 * <Surface variant="raised" interactive onClick={handleClick}>Carte cliquable</Surface>
 */
export function Surface({ className, variant, interactive, ...props }: SurfaceProps) {
  return <div className={cn(surfaceVariants({ variant, interactive }), className)} {...props} />;
}

export { surfaceVariants };
